import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { paymentsMessages } from '../../utils/messages';
import { Payment } from 'src/features/payments/utils/types';
import { useDeletePayment } from 'src/features/payments/api/useDeletePayment';
import { LIST_PAYMENTS_RESOURCE } from 'src/features/payments/api/useListPayments';
import { PAYMENT_RESOURCE } from 'src/features/payments/api/useGetPayment';
import { useNavigate } from 'react-router-dom';

interface DeletePaymentFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPayment: Payment;
    children?: React.ReactNode;
}

function DeletePaymentFormModal<TData>({ selectedPayment, children }: DeletePaymentFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const navigate = useNavigate();

    const { mutate: deletePayment } = useDeletePayment({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_PAYMENTS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [PAYMENT_RESOURCE] });
            navigate(`/payments`);
            setIsModalOpen(false);
            toast({
                title: formatMessage(paymentsMessages.deletePaymentSuccess),
                description: formatMessage(paymentsMessages.deletePaymentSuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...paymentsMessages.deletePayment },
                subTitle: {
                    ...paymentsMessages.deletePaymentSubtitle,
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...paymentsMessages.confirmDeletePayment },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: paymentsMessages.deletePayment,
                callbackFn: () => {
                    return (
                        deletePayment({
                            paymentId: selectedPayment.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeletePaymentFormModal;
